import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTeamStore } from '../store/teamStore';
import { toast } from 'react-hot-toast';
import { Plus, Calendar, Trophy, ArrowRight, Home, Plane } from 'lucide-react';
import { gameService, gameResult, type Game } from '../services/gameService';
import { CreateGameWizard } from './games/CreateGameWizard';
import PageHeader from './layout/PageHeader';
import { hasGames, getEventLabel } from '../data/sportRulesets';

export const GamesManagement: React.FC = () => {
  const { currentTeam, fetchAllTeams } = useTeamStore();
  const navigate = useNavigate();
  const eventLabel = getEventLabel(currentTeam?.sport);
  const [games, setGames] = useState<Game[]>([]);
  /** Teams hydrate from API on refresh; without this Games never loads and spinner hangs. */
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [gamesLoading, setGamesLoading] = useState(false);
  const [showCreateWizard, setShowCreateWizard] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setTeamsLoading(true);
      await fetchAllTeams();
      if (!cancelled) setTeamsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchAllTeams]);

  const loadGames = useCallback(async () => {
    if (!currentTeam?.id) return;
    setGamesLoading(true);
    try {
      const gamesData = await gameService.getGames(currentTeam.id);
      setGames(gamesData);
    } catch (error) {
      console.error('Error loading games:', error);
      toast.error('Failed to load games');
    } finally {
      setGamesLoading(false);
    }
  }, [currentTeam?.id]);

  useEffect(() => {
    if (teamsLoading || !currentTeam?.id) return;
    void loadGames();
  }, [teamsLoading, currentTeam?.id, loadGames]);

  const handleGameCreated = (game: Game) => {
    loadGames();
    // Navigate to game detail view
    navigate(`/games/${game.id}`);
  };

  if (teamsLoading || gamesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!currentTeam) {
    return (
      <div className="container mx-auto px-4 py-4 text-center">
        <Calendar className="mx-auto text-gray-400 mb-4" size={48} />
        <p className="text-gray-700 mb-2">No team is selected yet.</p>
        <p className="text-sm text-gray-500 mb-4">Open Home and pick your team first, then return to {eventLabel.plural}.</p>
        <Link to="/dashboard" className="btn-primary inline-block">
          Go to Home
        </Link>
      </div>
    );
  }

  if (!hasGames(currentTeam?.sport)) {
    return (
      <div className="text-center py-12">
        <Trophy className="mx-auto text-gray-400 mb-4" size={48} />
        <p className="text-gray-600">{eventLabel.plural} aren&apos;t available for {currentTeam?.sport ?? 'this'} teams yet.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-4">
      <PageHeader
        title={eventLabel.plural}
        icon={<Trophy className="h-6 w-6" />}
        actions={
          <button
            onClick={() => setShowCreateWizard(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={16} />
            Create {eventLabel.singular}
          </button>
        }
      />

      {games.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Calendar className="mx-auto text-gray-400 mb-4" size={48} />
          <p className="text-gray-600 mb-2">No {eventLabel.plural.toLowerCase()} created yet</p>
          <p className="text-sm text-gray-500 mb-4">Click "Create {eventLabel.singular}" to add a new {eventLabel.singular.toLowerCase()}</p>
          <button
            onClick={() => setShowCreateWizard(true)}
            className="btn-primary"
          >
            Create {eventLabel.singular}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {games.map((game) => (
            <div
              key={game.id}
              onClick={() => navigate(`/games/${game.id}`)}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {game.opponent_name}
                  </h3>
                  <div className="space-y-1 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {new Date(game.date + 'T00:00:00').toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                    </div>
                    {game.game_time && (
                      <div className="flex items-center gap-2">
                        <span>{new Date(`2000-01-01T${game.game_time}`).toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                        })}</span>
                      </div>
                    )}
                    {game.location && (
                      <div className="flex items-center gap-2">
                        <span>{game.location}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-shrink-0">
                  {game.home_away === 'home' ? (
                    <Home className="w-5 h-5 text-primary" />
                  ) : (
                    <Plane className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">
                    {game.home_away === 'home' ? 'Home' : 'Away'}
                  </span>
                  {(() => {
                    const result = gameResult(game);
                    if (result === null) return null;
                    return (
                      <>
                        <span
                          className={`text-xs font-bold px-1.5 py-0.5 rounded ${
                            result === 'W'
                              ? 'bg-green-100 text-green-700'
                              : result === 'L'
                              ? 'bg-red-100 text-red-600'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {result}
                        </span>
                        <span className="text-xs font-semibold tabular-nums text-gray-700">
                          {game.our_score} – {game.opponent_score}
                        </span>
                      </>
                    );
                  })()}
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Game Wizard */}
      {currentTeam && showCreateWizard && (
        <CreateGameWizard
          isOpen={showCreateWizard}
          onClose={() => setShowCreateWizard(false)}
          teamId={currentTeam.id}
          onGameCreated={handleGameCreated}
          sport={currentTeam.sport as any}
        />
      )}
    </div>
  );
};

